"use client";

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Loader2, Check, MessageSquare, PenLine, PauseCircle, SquareKanban } from "lucide-react";
import type { SessionNotificationState } from "@/hooks/use-notifications";
import {
  isTaskStatus,
  TASK_STATUS_DOT_CLASSES,
  TASK_STATUS_SHAPE_CLASSES,
} from "@/lib/task-status";
import type { TaskStatus } from "@/lib/project-types";

/* ----------------------------------------------------------------- */
/*  Props                                                              */
/* ----------------------------------------------------------------- */

export interface NotificationIndicatorProps {
  /** The session state produced by useNotifications (null = no state yet) */
  state: SessionNotificationState | null;
  /**
   * Ephemeral UI-owned status. `pending` is used while a voice note is still
   * uploading or transcribing, before a server-side run exists to report a
   * normal run status.
   */
  statusOverride?: "pending";
  /** Additional CSS class */
  className?: string;
  /**
   * Whether the session has a non-empty local draft (typed but unsent text).
   * When true and there is no active run, a pen icon is shown instead of the
   * neutral chat icon so the user can see at a glance which threads have
   * unsent work.
   */
  hasDraft?: boolean;
  /** Use task-specific idle behavior for a task-bound session. */
  taskSession?: boolean;
  /** The bound task's status, when it has been resolved. */
  taskStatus?: TaskStatus | null;
  /** Optional hook for testing or higher-level visual identification. */
  testId?: string;
  /** Icon to show in place of the neutral chat icon when idle. */
  idleIcon?: ReactNode;
}

/* ----------------------------------------------------------------- */
/*  Component                                                          */
/* ----------------------------------------------------------------- */

/**
 * Small badge widget placed next to a session row.
 *
 * Visual states (in priority order):
 *  - Blue spinner: a voice note is pending preparation (`pending`), or an
 *    agent is currently running (state.runStatus is "running" or "queued").
 *    Shown for ALL sessions — it reflects real-time activity, not read state.
 *  - Amber pause: the run paused for Human-in-the-Loop approval
 *    (runStatus === "paused").  The user needs to approve or reject
 *    before the run can continue.  Shown for ALL sessions.
 *  - Green checkmark: a turn finished while the user was away and hasn't
 *    been viewed yet (hasUnread && runStatus === "completed").  Only shown
 *    for locally-tracked sessions (created/interacted with on this client).
 *    Sessions created on other devices rely on the PWA push system for
 *    cross-device notifications, since we cannot reliably track read state
 *    across devices.  Once markSessionRead() clears hasUnread the indicator
 *    falls back to the neutral chat icon.
 *  - Amber pen: the session has a local draft (typed but unsent text).
 *    Shown only when there is no active run and no unread finished turn,
 *    so it never masks a more important state.
 *  - Task status dot: idle task-bound session whose task status is known.
 *  - Neutral chat icon: default / idle ordinary conversation.
 *
 * The component is accessible: screen-readers will announce the correct state
 * via aria-label.
 */
export function NotificationIndicator({
  state,
  statusOverride,
  hasDraft,
  className,
  taskSession = false,
  taskStatus,
  testId,
  idleIcon,
}: NotificationIndicatorProps) {
  const { t } = useTranslation();
  const runStatus = statusOverride ?? state?.runStatus;

  const isPending = runStatus === "pending";

  // Show spinner when there's an active (unsent / streaming) turn.
  // The backend RunStatus enum is: queued, running, paused, completed,
  // failed, cancelled.  "queued" and "running" are the in-flight states.
  const isRunning =
    isPending || runStatus === "running" || runStatus === "queued";

  // Pause icon when the run paused for Human-in-the-Loop approval.
  // The run is not finished — it's waiting for the user to approve or
  // reject a tool call before it can continue.
  const isPaused = runStatus === "paused";

  // Checkmark only for an UNREAD finished turn on a LOCALLY-TRACKED session —
  // a run that completed while the user was away and hasn't been viewed yet.
  // Only sessions created/interacted with on this client can show the
  // checkmark, since we can't track read state for sessions created on other
  // devices (those rely on the PWA push system). Once markSessionRead()
  // clears hasUnread, the indicator falls back to the neutral chat icon.
  const hasUnreadFinished =
    state?.hasUnread &&
    runStatus === "completed" &&
    state?.locallyTracked;

  // Draft indicator — only shown when no higher-priority state is active.
  // A draft is local unsent text; an in-flight run, a paused run, or an
  // unread response is more time-sensitive and takes precedence.
  const showDraft = hasDraft && !isRunning && !isPaused && !hasUnreadFinished;
  const resolvedTaskStatus = taskSession
    && typeof taskStatus === "string"
    && isTaskStatus(taskStatus)
    ? taskStatus
    : null;
  const taskStatusDotClass = resolvedTaskStatus
    ? TASK_STATUS_DOT_CLASSES[resolvedTaskStatus]
    : null;
  const taskStatusShapeClass = resolvedTaskStatus
    ? TASK_STATUS_SHAPE_CLASSES[resolvedTaskStatus]
    : null;

  return (
    <span
      className={cn(
        "inline-flex size-4 items-center justify-center",
        className,
      )}
      data-testid={testId}
      aria-label={
        isRunning
          ? isPending
            ? t("notifications.voiceNotePending")
            : t("notifications.agentRunning")
          : isPaused
            ? t("notifications.paused")
            : hasUnreadFinished
              ? t("notifications.newResponse")
              : showDraft
                ? t("notifications.hasDraft")
                : state
                  ? t("notifications.noNotifications")
                  : t("notifications.noActivity")
      }
    >
      {isRunning ? (
        <Loader2 className="size-3 animate-spin text-blue-500" />
      ) : isPaused ? (
        <PauseCircle className="size-3.5 text-amber-500" />
      ) : hasUnreadFinished ? (
        <Check className="size-3 text-green-500" />
      ) : showDraft ? (
        <PenLine className="size-3.5 text-amber-500" />
      ) : (
        // Task sessions retain normal activity states above. When their task
        // status is not available yet, preserve the existing kanban fallback.
        taskSession ? (
          taskStatusDotClass ? (
            <span
              className={cn(
                "size-2 shrink-0 rounded-full",
                taskStatusDotClass,
                taskStatusShapeClass,
              )}
              aria-hidden="true"
            />
          ) : (
            <SquareKanban className="size-4 shrink-0 text-primary" />
          )
        ) : idleIcon ?? (
          <MessageSquare className="size-4 shrink-0 text-muted-foreground" />
        )
      )}
    </span>
  );
}
